import GradientButton from '../../components/GradientButton'

import type {
  LotStoryGroupShareReviewPreflightView,
  TranslateFn
} from './lotStoryLibraryTypes'

type LotStoryGroupShareReviewPreflightCardProps = {
  t: TranslateFn
  preflight: LotStoryGroupShareReviewPreflightView
  testIdPrefix: string
  onRecordReview: () => void
  onRecordApproval: () => void
  onRecordReleaseGate: () => void
  onPromoteToGroup: () => void
}

const getTestId = (prefix: string, suffix?: string) => suffix ? `${prefix}-${suffix}` : prefix

export default function LotStoryGroupShareReviewPreflightCard({
  t,
  preflight,
  testIdPrefix,
  onRecordReview,
  onRecordApproval,
  onRecordReleaseGate,
  onPromoteToGroup
}: Readonly<LotStoryGroupShareReviewPreflightCardProps>) {
  const isLoading = preflight.status === 'loading' || preflight.status === 'idle'
  const statusMessage = preflight.status === 'allowed'
    ? t('lots.story.groupShareReview.status.allowed')
    : t('lots.story.groupShareReview.status.denied')
  const approvalStatusMessage = preflight.approvalReady
    ? t('lots.story.groupShareReview.approvalStatus.allowed')
    : t('lots.story.groupShareReview.approvalStatus.denied')
  const releaseGateStatusMessage = preflight.releaseGateReady
    ? t('lots.story.groupShareReview.releaseGateStatus.allowed')
    : t('lots.story.groupShareReview.releaseGateStatus.denied')
  const promotionStatusMessage = preflight.promotionReady
    ? t('lots.story.groupShareReview.promotionStatus.allowed')
    : preflight.promotionRecorded
      ? t('lots.story.groupShareReview.promotionStatus.promoted')
      : t('lots.story.groupShareReview.promotionStatus.denied')

  return (
    <div
      data-testid={getTestId(testIdPrefix)}
      style={{
        border: '1px solid #bfdbfe',
        borderRadius: 10,
        backgroundColor: '#f0f9ff',
        padding: 12,
        display: 'grid',
        gap: 8
      }}
    >
      <div style={{ display: 'grid', gap: 4 }}>
        <strong style={{ fontSize: 12, color: '#0369a1' }}>{t('lots.story.groupShareReview.title')}</strong>
        <div style={{ fontSize: 13, color: '#0369a1' }}>{t('lots.story.groupShareReview.subtitle')}</div>
      </div>

      {isLoading ? (
        <div data-testid={getTestId(testIdPrefix, 'status')} style={{ fontSize: 13, color: '#0369a1', fontWeight: 700 }}>
          {t('lots.story.groupShareReview.loading')}
        </div>
      ) : preflight.errorMessage ? (
        <div data-testid={getTestId(testIdPrefix, 'status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
          {preflight.errorMessage}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6, fontSize: 13, color: '#0c4a6e' }}>
          <div data-testid={getTestId(testIdPrefix, 'selected-group')}>
            <strong>{t('lots.story.groupShareReview.selectedGroup')}:</strong> {preflight.selectedGroupLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'saved-group')}>
            <strong>{t('lots.story.groupShareReview.savedGroup')}:</strong> {preflight.savedGroupLabel || t('lots.story.groupShareShell.notSaved')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'saved-match')}>
            <strong>{t('lots.story.groupShareReview.savedMatch')}:</strong> {preflight.selectedGroupMatchesSavedIntent ? t('lots.story.groupShareReview.savedMatch.yes') : t('lots.story.groupShareReview.savedMatch.no')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'identity')}>
            <strong>{t('lots.story.groupShareReview.identity')}:</strong> {preflight.identityLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'role')}>
            <strong>{t('lots.story.groupShareReview.tenantRole')}:</strong> {preflight.roleLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'visibility')}>
            <strong>{t('lots.story.groupShareReview.visibility')}:</strong> {preflight.artifactVisibilityLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'readiness')}>
            <strong>{t('lots.story.groupShareReview.readiness')}:</strong> {preflight.artifactReadinessLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'groups')}>
            <strong>{t('lots.story.groupShareReview.eligibleGroups')}:</strong> {preflight.eligibleGroupCount}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'partners')}>
            <strong>{t('lots.story.groupShareReview.partnerOrganizations')}:</strong> {preflight.partnerOrganizationsLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'reasons')}>
            <strong>{t('lots.story.groupShareReview.blockingReasons')}:</strong> {preflight.blockingReasonLabels.length > 0
              ? preflight.blockingReasonLabels.join('; ')
              : t('lots.story.groupPreflight.none')}
          </div>
          {preflight.reviewRecorded ? (
            <>
              <div data-testid={getTestId(testIdPrefix, 'reviewed-at')}>
                <strong>{t('lots.story.groupShareReview.reviewedAt')}:</strong> {preflight.reviewRecordedAtLabel || t('lots.story.groupPreflight.none')}
              </div>
              <div data-testid={getTestId(testIdPrefix, 'reviewed-by')}>
                <strong>{t('lots.story.groupShareReview.reviewedBy')}:</strong> {preflight.reviewRecordedByLabel || t('lots.story.groupPreflight.none')}
              </div>
            </>
          ) : null}
          <div
            data-testid={getTestId(testIdPrefix, 'status')}
            style={{
              color: preflight.status === 'allowed' ? '#166534' : '#b91c1c',
              fontWeight: 700
            }}
          >
            {statusMessage}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'approval-readiness')}>
            <strong>{t('lots.story.groupShareReview.approvalReadiness')}:</strong> {approvalStatusMessage}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'approval-reasons')}>
            <strong>{t('lots.story.groupShareReview.approvalBlockingReasons')}:</strong> {preflight.approvalBlockingReasonLabels.length > 0
              ? preflight.approvalBlockingReasonLabels.join('; ')
              : t('lots.story.groupPreflight.none')}
          </div>
          {preflight.approvalRecorded ? (
            <>
              <div data-testid={getTestId(testIdPrefix, 'approved-at')}>
                <strong>{t('lots.story.groupShareReview.approvedAt')}:</strong> {preflight.approvalRecordedAtLabel || t('lots.story.groupPreflight.none')}
              </div>
              <div data-testid={getTestId(testIdPrefix, 'approved-by')}>
                <strong>{t('lots.story.groupShareReview.approvedBy')}:</strong> {preflight.approvalRecordedByLabel || t('lots.story.groupPreflight.none')}
              </div>
            </>
          ) : null}
          <div data-testid={getTestId(testIdPrefix, 'release-gate-readiness')}>
            <strong>{t('lots.story.groupShareReview.releaseGateReadiness')}:</strong> {releaseGateStatusMessage}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'release-gate-reasons')}>
            <strong>{t('lots.story.groupShareReview.releaseGateBlockingReasons')}:</strong> {preflight.releaseGateBlockingReasonLabels.length > 0
              ? preflight.releaseGateBlockingReasonLabels.join('; ')
              : t('lots.story.groupPreflight.none')}
          </div>
          {preflight.releaseGateRecorded ? (
            <>
              <div data-testid={getTestId(testIdPrefix, 'release-gated-at')}>
                <strong>{t('lots.story.groupShareReview.releaseGatedAt')}:</strong> {preflight.releaseGateRecordedAtLabel || t('lots.story.groupPreflight.none')}
              </div>
              <div data-testid={getTestId(testIdPrefix, 'release-gated-by')}>
                <strong>{t('lots.story.groupShareReview.releaseGatedBy')}:</strong> {preflight.releaseGateRecordedByLabel || t('lots.story.groupPreflight.none')}
              </div>
            </>
          ) : null}
          <div data-testid={getTestId(testIdPrefix, 'promotion-readiness')}>
            <strong>{t('lots.story.groupShareReview.promotionReadiness')}:</strong> {promotionStatusMessage}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'promotion-reasons')}>
            <strong>{t('lots.story.groupShareReview.promotionBlockingReasons')}:</strong> {preflight.promotionBlockingReasonLabels.length > 0
              ? preflight.promotionBlockingReasonLabels.join('; ')
              : t('lots.story.groupPreflight.none')}
          </div>
          {preflight.promotionRecorded ? (
            <>
              <div data-testid={getTestId(testIdPrefix, 'promoted-at')}>
                <strong>{t('lots.story.groupShareReview.promotedAt')}:</strong> {preflight.promotedAtLabel || t('lots.story.groupPreflight.none')}
              </div>
              <div data-testid={getTestId(testIdPrefix, 'promoted-by')}>
                <strong>{t('lots.story.groupShareReview.promotedBy')}:</strong> {preflight.promotedByLabel || t('lots.story.groupPreflight.none')}
              </div>
            </>
          ) : null}
          {preflight.actionErrorMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'review-status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
              {preflight.actionErrorMessage}
            </div>
          ) : preflight.actionStatusMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'review-status')} style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
              {preflight.actionStatusMessage}
            </div>
          ) : null}
          {preflight.approvalActionErrorMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'approval-status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
              {preflight.approvalActionErrorMessage}
            </div>
          ) : preflight.approvalActionStatusMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'approval-status')} style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
              {preflight.approvalActionStatusMessage}
            </div>
          ) : null}
          {preflight.releaseGateActionErrorMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'release-gate-status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
              {preflight.releaseGateActionErrorMessage}
            </div>
          ) : preflight.releaseGateActionStatusMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'release-gate-status')} style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
              {preflight.releaseGateActionStatusMessage}
            </div>
          ) : null}
          {preflight.promotionActionErrorMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'promotion-status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
              {preflight.promotionActionErrorMessage}
            </div>
          ) : preflight.promotionActionStatusMessage ? (
            <div data-testid={getTestId(testIdPrefix, 'promotion-status')} style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
              {preflight.promotionActionStatusMessage}
            </div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <GradientButton
              type="button"
              onClick={onRecordReview}
              disabled={!preflight.canRecordReview || preflight.recording || preflight.reviewRecorded}
              data-testid={getTestId(testIdPrefix, 'review-button')}
            >
              {preflight.reviewRecorded
                ? t('lots.story.groupShareReview.reviewedButton')
                : preflight.recording
                  ? t('lots.story.groupShareReview.reviewing')
                  : t('lots.story.groupShareReview.reviewButton')}
            </GradientButton>
            <GradientButton
              type="button"
              onClick={onRecordApproval}
              disabled={!preflight.canRecordApproval || preflight.approvalRecording || preflight.approvalRecorded}
              data-testid={getTestId(testIdPrefix, 'approval-button')}
            >
              {preflight.approvalRecorded
                ? t('lots.story.groupShareReview.approvedButton')
                : preflight.approvalRecording
                  ? t('lots.story.groupShareReview.approving')
                  : t('lots.story.groupShareReview.approvalButton')}
            </GradientButton>
            <GradientButton
              type="button"
              onClick={onRecordReleaseGate}
              disabled={!preflight.canRecordReleaseGate || preflight.releaseGateRecording || preflight.releaseGateRecorded}
              data-testid={getTestId(testIdPrefix, 'release-gate-button')}
            >
              {preflight.releaseGateRecorded
                ? t('lots.story.groupShareReview.releaseGatedButton')
                : preflight.releaseGateRecording
                  ? t('lots.story.groupShareReview.releaseGating')
                  : t('lots.story.groupShareReview.releaseGateButton')}
            </GradientButton>
            <GradientButton
              type="button"
              onClick={onPromoteToGroup}
              disabled={!preflight.canPromoteToGroup || preflight.promotionRecording || preflight.promotionRecorded}
              data-testid={getTestId(testIdPrefix, 'promote-button')}
            >
              {preflight.promotionRecorded
                ? t('lots.story.groupShareReview.promotedButton')
                : preflight.promotionRecording
                  ? t('lots.story.groupShareReview.promoting')
                  : t('lots.story.groupShareReview.promoteButton')}
            </GradientButton>
          </div>
        </div>
      )}
    </div>
  )
}